import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Stack,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import {
  ArrowBack,
  Block,
  Build,
  Close,
  Edit,
  ForwardToInbox,
  KeyboardArrowDown,
  MarkEmailRead,
  PostAdd,
  Hotel,
  LocalActivity,
  Info,
  ReceiptLong,
  Payment,
} from "@mui/icons-material";
import {
  listAllHotelAgents,
  listBookingDetails,
  resetAllHotelAgents,
  resetBookingDetails,
  updateDayWiseRateInRooms,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { StatusChip } from "../../components/Chips";
import { tokens } from "../../theme";
import UpdateBookingDatesModalContent from "./update-booking-dates-modal";
import SwapRoomModalContent from "./swap-room-modal";
import CancelBookingModal from "./cancel-booking";
import UpdateBookingStatus from "./update-booking-status";
import BookingUpdation from "./bookingUpdation";
import RoomDetails from "./roomDetails";
import ResendEmailsModal from "./resend-emails";
import moment from "moment";
import BookingActionButton from "./BookingActionButton";
import Activities from "./Activities";
import { getOtaDisplayName, isPmsConnected } from "../../utils";
import AddUpdateActivitiesButton from "./AddUpdateActivitiesButton";
import BillingTab from "./billing/BillingTab";
import AdvancePaymentTab from "./billing/AdvancePaymentTab";
import { DayWiseRatesModal } from "../bookings/dayWiseRates";

// --- Reusable Components for Tabs ---

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`booking-tabpanel-${index}`}
      aria-labelledby={`booking-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `booking-tab-${index}`,
    "aria-controls": `booking-tabpanel-${index}`,
  };
}

// Configuration for Tabs
const TABS_CONFIG = [
  { label: "Booking Info", icon: <Info fontSize="small" /> },
  { label: "Room Details", icon: <Hotel fontSize="small" /> },
  { label: "Activities", icon: <LocalActivity fontSize="small" /> },
  { label: "Billing", icon: <ReceiptLong fontSize="small" /> }, // dont change label using it to conditionally render tab
  { label: "Advance Payment", icon: <Payment fontSize="small" /> },
];

function BookingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const bookingFromLabels = {
    direct_booking: "Direct Booking",
    walk_in: "Walk In",
    friends_family: "Friends/Family"
  };

  const getBookingFromLabel = (bookingFrom) => {
    if (!bookingFrom) return "-";

    if (bookingFromLabels[bookingFrom]) {
      return bookingFromLabels[bookingFrom];
    }

    return bookingFrom
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // --- Logic State ---
  const [updation, setUpdation] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  // --- Tab State ---
  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const [open, setOpen] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [updateStatusModal, setUpdateStatusModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [resendEmailModal, setResendEmailModal] = useState(false);
  const [resendUpdatedEmailModal, setResendUpdatedEmailModal] = useState(false);
  const handleClose = () => setOpen(false);
  const [dayWiseModalOpen, setDayWiseModalOpen] = useState(false);
  const [savingRates, setSavingRates] = useState(false);

  const { bookingDetails, agentsList, billing_enabled } = useSelector(
    ({
      booking: { bookingDetails },
      agents: {
        agents: { list },
      },
      registration,
    }) => ({
      bookingDetails,
      agentsList: list,
      billing_enabled: registration?.hotelProfile?.billing_enabled ?? false,
    }),
  );
  const activityTotal =
    bookingDetails?.activity_price + bookingDetails?.activity_tax || 0;

  const isEligibleForAction = ["pending", "checked_in"].includes(
    bookingDetails?.process_status,
  );
  const showActionButton = isEligibleForAction;

  const handleSaveDayWiseRates = async (roomsPayload) => {
    await dispatch(updateDayWiseRateInRooms(roomsPayload));
    dispatch(listBookingDetails({ bookingId: id }));
    setDayWiseModalOpen(false);
  };

  useEffect(() => {
    document.title = "Booking details";
    window.scrollTo(0, 0);
    dispatch(resetBookingDetails());
    dispatch(listBookingDetails({ bookingId: id }));
    dispatch(resetAllHotelAgents());
    dispatch(listAllHotelAgents({ isForDropdown: true }));
  }, [dispatch, id, updation]);

  const modalStyle = {
    height: "100%",
    width: "100%",
    overflow: "auto",
    outline: "none",
    boxShadow: 24,
    p: 2,
  };

  const visibleTabs = TABS_CONFIG.filter((tab) => {
    // Adjust "Billing" to match exactly what is written in your TABS_CONFIG
    if (tab.label === "Billing" && !billing_enabled) {
      return false;
    }
    return true;
  });

  return (
    <Box m="20px" mb={15}>
      {/* --- HEADER SECTION --- */}
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"flex-end"}
        spacing={2}
        m="20px 0"
      >
        {!updation && (
          <Button size="small" variant="outlined" onClick={() => navigate(-1)}>
            <ArrowBack fontSize="small" />
          </Button>
        )}
        <Typography fontWeight="bold" variant="h4">
          {updation && "UPDATE"} BOOKING DETAILS
        </Typography>
        {!updation ? (
          <Button
            size="small"
            variant="outlined"
            endIcon={<KeyboardArrowDown />}
            onClick={handleMenuClick}
          >
            <b>Update</b>
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setUpdation(false)}
            >
              <Close fontSize="small" />
            </Button>
            <Button
              size="small"
              type="submit"
              form="bookingUpdation"
              variant="contained"
            >
              <b>Save</b>
            </Button>
          </Stack>
        )}

        {/* --- MENU DROPDOWN --- */}
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            disabled={["checkin", "cancelled"].includes(bookingDetails?.status)}
            onClick={() => navigate(`/update-checkin/${id}`)}
          >
            <ListItemIcon>
              <PostAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Check-in</ListItemText>
          </MenuItem>

          <MenuItem
            disabled={bookingDetails?.status === "cancelled" || isPmsConnected()}
            onClick={() => {
              handleMenuClose();
              setUpdation(true);
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Update Details</ListItemText>
          </MenuItem>

          <MenuItem
            disabled={bookingDetails?.status === "checkin" || isPmsConnected()}
            onClick={() => {
              handleMenuClose();
              setUpdateStatusModal(true);
            }}
          >
            <ListItemIcon>
              <Build fontSize="small" />
            </ListItemIcon>
            <ListItemText>Update Status</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={bookingDetails?.status === "cancelled"}
            onClick={() => {
              handleMenuClose();
              setResendEmailModal(true);
            }}
          >
            <ListItemIcon>
              <MarkEmailRead fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resend Confirmation Email</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={
              bookingDetails?.status === "cancelled" ||
              bookingDetails?.createdAt === bookingDetails?.updatedAt
            }
            onClick={() => {
              handleMenuClose();
              setResendUpdatedEmailModal(true);
              setResendEmailModal(true);
            }}
          >
            <ListItemIcon>
              <ForwardToInbox fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send Update Email</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>

      {/* --- CONTENT AREA --- */}
      <Box
        width="100%"
        p={2}
        borderRadius={2}
        backgroundColor={colors.primary[400]}
      >
        {!updation ? (
          <>
            {/* 1. TABS HEADER */}
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="booking details tabs"
                textColor="secondary"
                indicatorColor="secondary"
                variant="scrollable" // Enables scrolling behavior
                scrollButtons="auto" // Shows scroll arrows if tabs overflow
                allowScrollButtonsMobile // Ensures scroll buttons show
              >
                {visibleTabs.map((tab, index) => (
                  <Tab
                    key={index}
                    label={tab.label}
                    iconPosition="start"
                    icon={tab.icon}
                    {...a11yProps(index)}
                  />
                ))}
              </Tabs>
            </Box>

            {/* 2. TAB CONTENT: BOOKING INFO (Index 0) */}
            <CustomTabPanel value={tabValue} index={0}>
              <Grid container rowSpacing={2} columnSpacing={{ md: 4, xs: 2 }}>
                {/* Global Actions for Booking */}
                <Grid item md={12} xs={12}>
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    {showActionButton ? (
                      <BookingActionButton bookingDetails={bookingDetails} />
                    ) : null}

                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<Block />}
                      color="error"
                      disabled={bookingDetails?.status === "cancelled"}
                      onClick={() => {
                        handleMenuClose();
                        setCancelModal(true);
                      }}
                    >
                      <b>Cancel Booking</b>
                    </Button>
                  </Stack>
                </Grid>

                {/* Left Column: Booking Stats */}
                <Grid item md={6} xs={12}>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Booking id:</Typography>
                      <Typography color={colors.greenAccent[500]}>
                        <small>{bookingDetails?.booking_id || "-"}</small>
                      </Typography>
                    </Stack>
                    {bookingDetails?.reference_id ? (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Reference id:</Typography>
                        <Typography>
                          <small>{bookingDetails?.reference_id ?? "-"}</small>
                        </Typography>
                      </Stack>
                    ) : null}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Check-in date:</Typography>
                      <Typography>
                        {bookingDetails?.checkin_date
                          ? moment
                              .unix(bookingDetails?.checkin_date)
                              .format("DD-MMM-YYYY")
                          : "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Check-out date:</Typography>
                      <Typography>
                        {bookingDetails?.checkout_date
                          ? moment
                              .unix(bookingDetails?.checkout_date)
                              .format("DD-MMM-YYYY")
                          : "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Booking from:</Typography>
                      <Typography>
                        {getOtaDisplayName(bookingDetails?.booking_from?.replace("_", " ")) || "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Booked on:</Typography>
                      <Typography>
                        {bookingDetails?.booked_date || "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Booking status:</Typography>
                      <StatusChip status={bookingDetails?.status} />
                    </Stack>
                    {["checked_out", "billed"].includes(
                      bookingDetails?.process_status,
                    ) && (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Process status:</Typography>
                        <StatusChip status={bookingDetails?.process_status} />
                      </Stack>
                    )}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Total rooms:</Typography>
                      <Typography>
                        {bookingDetails?.total_rooms || "0"}
                      </Typography>
                    </Stack>
                    {bookingDetails?.extra_beds ? (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Extra beds:</Typography>
                        <Typography>
                          {bookingDetails?.extra_beds ?? "-"}
                        </Typography>
                      </Stack>
                    ) : null}
                    {activityTotal > 0 ? (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Activity Total:</Typography>
                        <Typography>₹{activityTotal}</Typography>
                      </Stack>
                    ) : null}
                    {bookingDetails?.custom_price ? (
                      <>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width="40%">Special price:</Typography>
                          <Typography>
                            ₹{bookingDetails?.custom_price || 0}
                          </Typography>
                        </Stack>

                        {activityTotal > 0 && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width="40%">Total price:</Typography>
                            <Typography>
                              ₹
                              {Number(activityTotal) +
                                Number(bookingDetails?.custom_price || 0)}
                            </Typography>
                          </Stack>
                        )}
                      </>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Total price:</Typography>
                        <Typography>
                          ₹{bookingDetails?.total_price || "0"}
                        </Typography>
                      </Stack>
                    )}
                    {!!(
                      bookingDetails?.gst_amount || bookingDetails?.total_tax
                    ) && (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Tax amount:</Typography>
                        <Typography>
                          ₹ {bookingDetails?.gst_amount ||
                            bookingDetails?.total_tax}
                        </Typography>
                      </Stack>
                    )}
                    {bookingDetails?.tcs_amount ? (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>TCS Amount:</Typography>
                        <Typography>
                          ₹{bookingDetails?.tcs_amount || "0"}
                        </Typography>
                      </Stack>
                    ) : null}
                    {bookingDetails?.commission ? (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Commission:</Typography>
                        <Typography>
                          ₹{bookingDetails?.commission || "0"}
                        </Typography>
                      </Stack>
                    ) : null}
                    {bookingDetails?.coupon_code &&
                      bookingDetails?.coupon_discount && (
                        <>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>Coupon Code:</Typography>
                            <Typography>
                              {bookingDetails?.coupon_code}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>
                              Coupon Discount:
                            </Typography>
                            <Typography>
                              ₹{bookingDetails?.coupon_discount}
                            </Typography>
                          </Stack>
                        </>
                      )}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Paid amount:</Typography>
                      <Typography>
                        ₹{bookingDetails?.paid_amount || "0"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Unpaid amount:</Typography>
                      <Typography>
                        ₹{bookingDetails?.unpaid_amount || "0"}
                      </Typography>
                    </Stack>
                    {bookingDetails?.cut_off_days ? (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Cut off period:</Typography>
                        <Typography>
                          {bookingDetails?.cut_off_days || "0"} Day(s)
                        </Typography>
                      </Stack>
                    ) : null}
                    {bookingDetails?.meal_plan_id && (
                      <>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width={"40%"}>Meal plan:</Typography>
                          <Typography>
                            {bookingDetails?.meal_plan_id?.name || "-"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width={"40%"}>
                            Total meals amount:
                          </Typography>
                          <Typography>
                            {bookingDetails?.meal_plan_id?.rate || 0}
                          </Typography>
                        </Stack>
                      </>
                    )}
                    {bookingDetails?.remarks && (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Remarks:</Typography>
                        <Typography>
                          {bookingDetails?.remarks || "-"}
                        </Typography>
                      </Stack>
                    )}
                    {bookingDetails?.preference && (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>Preference:</Typography>
                        <Typography>
                          {bookingDetails?.preference || "-"}
                        </Typography>
                      </Stack>
                    )}
                    {bookingDetails?.billing_instruction && (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"40%"}>
                          Billing Instruction:
                        </Typography>
                        <Typography>
                          {bookingDetails?.billing_instruction || "-"}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Grid>

                {/* Right Column: Guest & Agent Info */}
                <Grid item md={6} xs={12}>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Name:</Typography>
                      <Typography>
                        {bookingDetails?.title || ""}{" "}
                        {bookingDetails?.name || "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Email:</Typography>
                      <Typography>{bookingDetails?.email || "-"}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Mobile:</Typography>
                      <Typography>
                        {bookingDetails?.guest_mobile || "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Phone:</Typography>
                      <Typography>
                        {bookingDetails?.guest_phone || "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"40%"}>Address:</Typography>
                      <Typography>
                        {bookingDetails?.guest_address || "-"}
                      </Typography>
                    </Stack>
                    {bookingDetails?.agent_id?.first_name && (
                      <>
                        <Stack mt={2}>
                          <Typography
                            variant="h4"
                            color={colors.greenAccent[600]}
                            fontWeight={700}
                          >
                            Agent Info
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width={"40%"}>Name:</Typography>
                          <Typography>
                            {bookingDetails?.agent_id?.first_name
                              ? `${bookingDetails?.agent_id?.first_name} ${
                                  bookingDetails?.agent_id?.last_name || ""
                                }`
                              : "-"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width={"40%"}>Email:</Typography>
                          <Typography>
                            {bookingDetails?.agent_id?.email || "-"}
                          </Typography>
                        </Stack>
                        {bookingDetails?.agent_id?.mobile && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>Mobile:</Typography>
                            <Typography>
                              {bookingDetails?.agent_id?.mobile || "-"}
                            </Typography>
                          </Stack>
                        )}
                        {/* Address */}
                        {bookingDetails?.agent_id?.address && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>Address:</Typography>
                            <Typography>
                              {bookingDetails?.agent_id?.address}
                            </Typography>
                          </Stack>
                        )}

                        {/* GST Number */}
                        {bookingDetails?.agent_id?.gst_number && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>GST Number:</Typography>
                            <Typography>
                              {bookingDetails?.agent_id?.gst_number}
                            </Typography>
                          </Stack>
                        )}
                      </>
                    )}
                    {bookingDetails?.sub_agent_id?.email && (
                      <>
                        <Stack mt={2}>
                          <Typography
                            variant="h4"
                            color={colors.greenAccent[600]}
                            fontWeight={700}
                          >
                            Sub Agent Info
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width={"40%"}>Name:</Typography>
                          <Typography>
                            {bookingDetails?.sub_agent_id?.name || "-"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width={"40%"}>Email:</Typography>
                          <Typography>
                            {bookingDetails?.sub_agent_id?.email || "-"}
                          </Typography>
                        </Stack>
                        {bookingDetails?.sub_agent_id?.mobile && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>Mobile:</Typography>
                            <Typography>
                              {bookingDetails?.sub_agent_id?.mobile || "-"}
                            </Typography>
                          </Stack>
                        )}
                      </>
                    )}
                    {bookingDetails?.company_id?.name && (
                      <>
                        <Stack mt={2}>
                          <Typography
                            variant="h4"
                            color={colors.greenAccent[600]}
                            fontWeight={700}
                          >
                            Company Info
                          </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width={"40%"}>Name:</Typography>
                          <Typography>
                            {bookingDetails?.company_id?.name || "-"}
                          </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography width={"40%"}>Email:</Typography>
                          <Typography>
                            {bookingDetails?.company_id?.email || "-"}
                          </Typography>
                        </Stack>

                        {bookingDetails?.company_id?.mobile && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>Mobile:</Typography>
                            <Typography>
                              {bookingDetails?.company_id?.mobile || "-"}
                            </Typography>
                          </Stack>
                        )}

                        {bookingDetails?.company_id?.address && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>Address:</Typography>
                            <Typography>
                              {bookingDetails?.company_id?.address || "-"}
                            </Typography>
                          </Stack>
                        )}

                        {bookingDetails?.company_id?.gst_number && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography width={"40%"}>GST Number:</Typography>
                            <Typography>
                              {bookingDetails?.company_id?.gst_number || "-"}
                            </Typography>
                          </Stack>
                        )}
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CustomTabPanel>

            {/* 3. TAB CONTENT: ROOM DETAILS (Index 1) */}
            <CustomTabPanel value={tabValue} index={1}>
              {/* <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h5" fontWeight="bold">
                  Assigned Rooms
                </Typography>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={bookingDetails?.status === "cancelled"}
                  onClick={() => setDayWiseModalOpen(true)}
                >
                  <b>Add Daily Rates</b>
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={bookingDetails?.status === "cancelled"}
                  onClick={() => {
                    navigate(`/unblock/${bookingDetails?.booking_id}`);
                  }}
                >
                  <b>Add / Remove Rooms</b>
                </Button>
              </Stack> */}
              <Stack direction="row" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight="bold">
                  Assigned Rooms
                </Typography>

                {/* Right-aligned buttons */}
                <Stack direction="row" spacing={1} ml="auto">
                  {billing_enabled && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={bookingDetails?.status === "cancelled"}
                      onClick={() => setDayWiseModalOpen(true)}
                    >
                      <b>Add Daily Rates</b>
                    </Button>
                  )}

                  <Button
                    size="small"
                    variant="outlined"
                    disabled={bookingDetails?.status === "cancelled"}
                    onClick={() => {
                      navigate(`/unblock/${bookingDetails?.booking_id}`);
                    }}
                  >
                    <b>Add / Remove Rooms</b>
                  </Button>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* WRAPPER FIX: 
                 Wrapping RoomDetails in a Grid container here fixes the 
                 full-width card issue without modifying RoomDetails.js 
              */}
              <Grid container spacing={2}>
                <RoomDetails
                  bookingId={id}
                  rooms={bookingDetails?.rooms}
                  isCustomPrice={bookingDetails?.custom_price ? true : false}
                  bookingFrom={bookingDetails?.booking_from}
                  colors={colors}
                  modalStyle={modalStyle}
                />
              </Grid>
            </CustomTabPanel>

            {/* 4. TAB CONTENT: ACTIVITIES (Index 2) */}
            <CustomTabPanel value={tabValue} index={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h5" fontWeight="bold">
                  Guest Activities
                </Typography>
                <AddUpdateActivitiesButton bookingDetails={bookingDetails} />
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Activities
                activities={bookingDetails.activities}
                colors={colors}
              />
            </CustomTabPanel>

            {/* Index 3: Show Billing ONLY if enabled */}
            {billing_enabled && (
              <CustomTabPanel value={tabValue} index={3}>
                <BillingTab bookingDetails={bookingDetails} colors={colors} />
              </CustomTabPanel>
            )}

            {/* Index 4 becomes Index 3 if billing is disabled */}
            <CustomTabPanel value={tabValue} index={billing_enabled ? 4 : 3}>
              <AdvancePaymentTab
                bookingDetails={bookingDetails}
                colors={colors}
              />
            </CustomTabPanel>
          </>
        ) : (
          /* UPDATION VIEW */
          <BookingUpdation
            bookingDetails={bookingDetails}
            setUpdation={setUpdation}
            colors={colors}
            dispatch={dispatch}
            agentsList={agentsList}
          />
        )}
      </Box>

      {/* --- MODALS --- */}
      <Modal open={open} onClose={handleClose} sx={modalStyle}>
        <>
          <SwapRoomModalContent
            bookingDetails={bookingDetails}
            handleClose={handleClose}
          />
        </>
      </Modal>
      <Modal
        open={updateModal}
        onClose={() => setUpdateModal(false)}
        sx={modalStyle}
      >
        <>
          <UpdateBookingDatesModalContent
            bookingDetails={bookingDetails}
            setUpdateModal={setUpdateModal}
          />
        </>
      </Modal>
      <Modal
        open={updateStatusModal}
        onClose={() => setUpdateStatusModal(false)}
        sx={modalStyle}
      >
        <>
          <UpdateBookingStatus
            bookingDetails={bookingDetails}
            setUpdateStatusModal={setUpdateStatusModal}
          />
        </>
      </Modal>
      <Modal
        open={resendEmailModal}
        onClose={() => {
          setResendUpdatedEmailModal(false);
          setResendEmailModal(false);
        }}
        sx={modalStyle}
      >
        <>
          <ResendEmailsModal
            bookingDetails={bookingDetails}
            setResendEmailModal={setResendEmailModal}
            isForUpdate={resendUpdatedEmailModal}
            setResendUpdatedEmailModal={setResendUpdatedEmailModal}
          />
        </>
      </Modal>
      <Modal
        open={cancelModal}
        onClose={() => setCancelModal(false)}
        sx={modalStyle}
      >
        <>
          <CancelBookingModal
            bookingDetails={bookingDetails}
            setCancelModal={setCancelModal}
          />
        </>
      </Modal>
      <DayWiseRatesModal
        open={dayWiseModalOpen}
        onClose={() => setDayWiseModalOpen(false)}
        bookingDetails={bookingDetails}
        onSave={handleSaveDayWiseRates} // Your GraphQL mutation handler
        loading={savingRates}
        colors={colors}
      />
    </Box>
  );
}

export default BookingDetails;
